import React, { useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Paper,
  Button,
} from '@mui/material';
import { styled } from '@mui/system';
import { motion } from 'framer-motion';
import SchoolIcon from '@mui/icons-material/School';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import GroupIcon from '@mui/icons-material/Group';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import youthPreview from '../../components/assets/NextGeneration/NextGenerationYouth.png';

/* —— Netflix-ish tokens —— */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.78)",
  border: "rgba(255,255,255,0.12)",
  red: "#E50914",
  redHover: "#b20710",
};
const RADIUS = 18;

/* Title */
const MainTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 900,
  fontSize: '2.6rem',
  color: COLORS.text,
  letterSpacing: "-0.02em",
  marginBottom: theme.spacing(1.25),
  textShadow: "0 1px 0 rgba(0,0,0,0.4)",
  [theme.breakpoints.down('md')]: {
    fontSize: '2rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
    textAlign: 'center',
  },
}));

/* Section container (motion) */
const SectionContainer = styled(motion.div)(({ theme }) => ({
  position: 'relative',
  background: COLORS.bg,
  fontFamily: 'Lexend, sans-serif',
  overflow: 'hidden',
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
  // subtle glows
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      "radial-gradient(1000px 420px at 8% 12%, rgba(229,9,20,0.14), transparent 60%), radial-gradient(900px 420px at 92% 8%, rgba(255,255,255,0.06), transparent 60%)",
    pointerEvents: 'none',
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '1.1rem',
  color: COLORS.textMuted,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    textAlign: 'center',
  },
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: '1.02rem',
  lineHeight: 1.7,
  color: COLORS.textMuted,
  marginBottom: theme.spacing(2.25),
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.98rem',
    textAlign: 'center',
  },
}));

/* Bullet card (panel) */
const BulletCard = styled(motion(Paper))(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  borderRadius: RADIUS,
  background: COLORS.panel,
  color: COLORS.text,
  border: `1px solid ${COLORS.border}`,
  boxShadow:
    "0 16px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset",
  transition: 'border-color 180ms ease, transform 220ms ease',
  cursor: 'default',
  '&:hover': {
    borderColor: COLORS.red,
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.25),
    borderRadius: 14,
    gap: theme.spacing(1.25),
  },
}));

/* Floating image */
const StyledImageWrapper = styled(motion.div)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
  },
}));

const StyledImage = styled(motion.img)(({ theme }) => ({
  width: '100%',
  maxWidth: 440,
  borderRadius: 16,
  display: 'block',
  boxShadow:
    "0 18px 60px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.06) inset",
  border: `1px solid ${COLORS.border}`,
  [theme.breakpoints.down('md')]: {
    maxWidth: 340,
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: 220,
    borderRadius: 12,
  },
}));

/* Buttons (keep names; swap to Netflix red aesthetics) */
const GradientButton = styled(motion(Button))(({ theme }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontSize: '1.08rem',
  fontWeight: 800,
  padding: theme.spacing(1.4, 3.4),
  marginRight: theme.spacing(2),
  marginTop: theme.spacing(2),
  background: COLORS.red,
  color: '#fff',
  border: 'none',
  boxShadow: '0 12px 28px rgba(229,9,20,0.35)',
  '&:hover': {
    background: COLORS.redHover,
    boxShadow: '0 16px 34px rgba(229,9,20,0.45)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.98rem',
    padding: theme.spacing(1.1, 2.4),
    marginRight: 0,
    width: '100%',
  },
}));

const OutlinedButton = styled(motion(Button))(({ theme }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontSize: '1.08rem',
  fontWeight: 800,
  padding: theme.spacing(1.35, 3.2),
  marginTop: theme.spacing(2),
  border: `2px solid ${COLORS.red}`,
  color: COLORS.text,
  background: 'transparent',
  boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
  '&:hover': {
    borderColor: COLORS.redHover,
    background: 'rgba(229,9,20,0.08)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.98rem',
    padding: theme.spacing(1.1, 2.4),
    width: '100%',
  },
}));

const NextGeneration: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const bulletItems = [
    {
      icon: <LiveTvIcon sx={{ fontSize: 28, color: COLORS.text }} />,
      text: 'Showcase their talents through live or recorded sessions',
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 28, color: COLORS.text }} />,
      text: 'Receive mentorship from experienced professionals',
    },
    {
      icon: <GroupIcon sx={{ fontSize: 28, color: COLORS.text }} />,
      text: 'Join a supportive audience of peers, fans, and educators',
    },
    {
      icon: <EmojiEventsIcon sx={{ fontSize: 28, color: COLORS.text }} />,
      text: 'Access sponsorship opportunities from institutions & brands',
    },
  ];

  return (
    <SectionContainer
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      viewport={{ once: true }}
    >
      <Grid
        container
        spacing={isMobile ? 2 : isTablet ? 4 : 6}
        alignItems="center"
        direction={isMobile ? 'column-reverse' : 'row'}
        maxWidth={1400}
        margin="0 auto"
        sx={{
          px: { xs: 2, sm: 3, md: 6 },
          width: '100%',
        }}
      >
        {/* LEFT — copy */}
        <Grid item xs={12} md={6}>
          <MainTitle>
            Our Commitment to Empowering the Next Generation
          </MainTitle>

          <SectionSubtitle>
            <Box component="span" sx={{ color: COLORS.red, fontWeight: 900 }}>
              StageLink Youth Program
            </Box>
          </SectionSubtitle>

          <Description>
            Inspiring young creators to{" "}
            <Box component="span" sx={{ color: COLORS.text, fontWeight: 700 }}>
              discover
            </Box>{" "}
            and{" "}
            <Box component="span" sx={{ color: COLORS.text, fontWeight: 700 }}>
              grow
            </Box>{" "}
            their talents — safely and confidently.
          </Description>

          <Description>
            <strong>StageLink Youth Program</strong> is a dedicated space within our platform where children and teens can:
          </Description>

          <Grid container spacing={1} mb={2}>
            {bulletItems.map((item, idx) => (
              <Grid item xs={12} key={idx}>
                <BulletCard
                  elevation={0}
                  initial={{ opacity: 0, x: -18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * idx, duration: 0.45 }}
                  viewport={{ once: true }}
                >
                  {item.icon}
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{
                      color: COLORS.textMuted,
                      ...(isMobile && { fontSize: '0.98rem' }),
                    }}
                  >
                    {item.text}
                  </Typography>
                </BulletCard>
              </Grid>
            ))}
          </Grid>

          <Description>
            From{" "}
            <Box component="span" sx={{ color: COLORS.text, fontWeight: 700 }}>
              live music covers
            </Box>{" "}
            to{" "}
            <Box component="span" sx={{ color: COLORS.text, fontWeight: 700 }}>
              digital art showcases
            </Box>{" "}
            and youth-led talk shows — we spotlight tomorrow’s creators today.
          </Description>
        </Grid>

        {/* RIGHT — visual */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            zIndex: 1,
            mb: isMobile ? 3 : 0,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <StyledImageWrapper
            animate={{ y: [0, -10, 0] }}
            transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
          >
            <StyledImage
              src={youthPreview}
              alt="Youth talent performing"
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.25, duration: 0.55 }}
              viewport={{ once: true }}
            />
          </StyledImageWrapper>
        </Grid>
      </Grid>

      {/* CTAs */}
      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        flexWrap="wrap"
        zIndex={2}
        justifyContent="center"
        sx={{ mt: { xs: 2, sm: 3, md: 4 }, gap: { xs: 2, sm: 3, md: 4 } }}
        px={2}
      >
        <GradientButton
          variant="contained"
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.98 }}
          href="/coming-soon"
          sx={{ mb: isMobile ? 1 : 0 }}
        >
          Explore the Youth Program →
        </GradientButton>

        <OutlinedButton
          variant="outlined"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
          href="/register"
        >
          Register Your Talent
        </OutlinedButton>
      </Box>
    </SectionContainer>
  );
};

export default NextGeneration;
